'use client';

import { AtClickable, AtHeader, AtIcon } from '@/components/atoms';
import { AtDropdown } from '@/components/atoms';
import { EIcons } from '../or-video-hero/or-video-hero.types';
import {
  EClickableSize,
  EClickableTypes,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { MlExteriorDrawer } from '@/components/molecules';
import { useGalleryData } from '../../../hooks/useGalleryData';
import Skeleton from 'react-loading-skeleton';

type THandleFilterChange = (filterKey: string, vals: string | string[]) => void;

interface IFilters {
  planSeries: string[];
  room: string[];
  exterior: string[];
  location: string[];
}

export interface OrImageGalleryFiltersProps {
  selectedFilters: IFilters;
  setSelectedFilters: React.Dispatch<React.SetStateAction<IFilters>>;
  setDrawerOpen: (open: boolean) => void;
  drawerOpen: boolean;
  handleFilterChange: THandleFilterChange;
}

function ImageGalleryFilterDropdowns({
  selectedFilters,
  setSelectedFilters,
  handleFilterChange,
}: {
  selectedFilters: IFilters;
  setSelectedFilters: React.Dispatch<React.SetStateAction<IFilters>>;
  handleFilterChange: THandleFilterChange;
}) {
  // Utility to sort options alphabetically by label
  const { filterValues, loading } = useGalleryData();

  if (loading || filterValues == null) {
    return (
      <div className="flex w-full flex-col gap-8 lg:grid lg:grid-cols-4">
        {/* Plan Series Dropdown */}
        <div className="w-full lg:col-span-1">
          <div className="mb-4 flex items-center justify-between lg:mb-0">
            <Skeleton
              containerClassName="w-1/3 block h-full inset-0 rounded-30"
              baseColor="var(--color-neutral-100)"
              highlightColor="var(--color-neutral-200)"
              className="inset-0 block h-full w-1/3 rounded-30"
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Room Dropdown */}
        <div className="w-full lg:col-span-1">
          <Skeleton
            containerClassName="w-1/3 block h-full inset-0 rounded-30"
            baseColor="var(--color-neutral-100)"
            highlightColor="var(--color-neutral-200)"
            className="inset-0 block h-full w-1/3 rounded-30"
            aria-hidden="true"
          />
        </div>

        {/* Exterior Dropdown */}
        <div className="w-full lg:col-span-1">
          <Skeleton
            containerClassName="w-1/3 block h-full inset-0 rounded-30"
            baseColor="var(--color-neutral-100)"
            highlightColor="var(--color-neutral-200)"
            className="inset-0 block h-full w-1/3 rounded-30"
            aria-hidden="true"
          />
        </div>

        {/* Location Dropdown */}
        <div className="w-full lg:col-span-1">
          <Skeleton
            containerClassName="w-1/3 block h-full inset-0 rounded-30"
            baseColor="var(--color-neutral-100)"
            highlightColor="var(--color-neutral-200)"
            className="inset-0 block h-full w-1/3 rounded-30"
            aria-hidden="true"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-8 lg:grid lg:grid-cols-4">
      {/* Plan Series Dropdown */}
      <div className="w-full lg:col-span-1">
        <div className="mb-4 flex items-center justify-between lg:mb-0">
          <AtHeader className="lg:mb-4" variant="h6">
            Plan Series
          </AtHeader>
          {/* Reset filters button (mobile only) */}
          <AtClickable
            className="lg:hidden"
            interactLocation="or-image-gallery-filters"
            type={EClickableTypes.LINK}
            variant={EClickableVariants.PRIMARY}
            size={EClickableSize.SM}
            onClick={() => {
              // Reset all filters
              setSelectedFilters({
                planSeries: [],
                room: [],
                exterior: [],
                location: [],
              });
            }}
            label="Reset Filters"
          />
        </div>
        <AtDropdown
          isMulti={true}
          hasBorder
          icon={EIcons.HOME_PLANS}
          label="Select A House Plan"
          options={filterValues.planSeriesOptions}
          value={selectedFilters.planSeries}
          // Update selected "planSeries" filters
          onChange={(vals) => {
            //filter 1of4: Plan Series Dropdown
            handleFilterChange('planSeries', vals);
          }}
        />
      </div>

      {/* Room Dropdown */}
      <div className="w-full lg:col-span-1">
        <AtHeader className="mb-4" variant="h6">
          Room
        </AtHeader>
        <AtDropdown
          isMulti={true}
          hasBorder
          icon={EIcons.LENS}
          label="All Rooms"
          options={filterValues.roomOptions}
          value={selectedFilters.room}
          onChange={(vals) => {
            //filter 2of4: Room Dropdown
            handleFilterChange('room', vals);
          }}
        />
      </div>

      {/* Exterior Dropdown */}
      <div className="w-full lg:col-span-1">
        <AtHeader className="mb-4" variant="h6">
          Exterior Styles
        </AtHeader>
        <AtDropdown
          isMulti={true}
          hasBorder
          icon={EIcons.EXTERIOR}
          label="All Styles"
          options={filterValues.exteriorOptions}
          value={selectedFilters.exterior}
          onChange={(vals) => {
            //filter 3of4: Exterior Dropdown
            handleFilterChange('exterior', vals);
          }}
        />
      </div>

      {/* Location Dropdown */}
      <div className="w-full lg:col-span-1">
        <AtHeader className="mb-4" variant="h6">
          Location
        </AtHeader>
        <AtDropdown
          isMulti={true}
          hasBorder
          icon={EIcons.LOCATION}
          label="All Locations"
          options={filterValues.locationOptions}
          value={selectedFilters.location}
          onChange={(vals) => {
            //filter 4of4: Location Dropdown
            handleFilterChange('location', vals);
          }}
        />
      </div>
    </div>
  );
}

export const OrImageGalleryFilters = ({
  selectedFilters,
  setSelectedFilters,
  setDrawerOpen,
  drawerOpen,
  handleFilterChange,
}: OrImageGalleryFiltersProps) => {
  return (
    <>
      {/* Main filter container */}
      <div className="shadow-floating-mobile flex flex-col items-center gap-4 rounded-30 px-[70px] py-6 lg:items-start lg:pb-0 lg:pt-16">
        <AtHeader variant="h2" element="h1" className="text-left lg:pb-8">
          Gallery
        </AtHeader>
        {/* Desktop Filters */}
        <div className="hidden w-full lg:flex">
          <ImageGalleryFilterDropdowns
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            handleFilterChange={handleFilterChange}
          />
        </div>
        {/* Desktop Reset Button */}
        <div className="hidden w-full justify-end pt-8 lg:flex">
          <AtClickable
            interactLocation="or-image-gallery-filters"
            type={EClickableTypes.LINK}
            variant={EClickableVariants.PRIMARY}
            size={EClickableSize.SM}
            onClick={() => {
              setSelectedFilters({
                planSeries: [],
                room: [],
                exterior: [],
                location: [],
              });
            }}
            label="Reset Filters"
          />
        </div>
      </div>

      {/* Mobile Filters (drawer) */}
      <div className="block lg:hidden">
        <button
          className="shadow-floating-mobile mt-5 flex w-full items-center justify-between rounded-30 px-8 py-4"
          onClick={() => {
            setDrawerOpen(true);
          }}
        >
          <AtHeader variant="h5" element="span">
            Filters
          </AtHeader>
          <AtIcon icon={EIcons.FILTER} color={'#322D24'} className="text-24" />
        </button>
        <MlExteriorDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        >
          <div className="p-6">
            <ImageGalleryFilterDropdowns
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              handleFilterChange={handleFilterChange}
            />
            <div className="flex w-full justify-center pt-8">
              <AtClickable
                interactLocation="or-image-gallery-filters"
                type={EClickableTypes.LINK}
                variant={EClickableVariants.PRIMARY}
                size={EClickableSize.SM}
                onClick={() => {
                  setSelectedFilters({
                    planSeries: [],
                    room: [],
                    exterior: [],
                    location: [],
                  });
                }}
                label="Reset Filters"
              />
            </div>
          </div>
        </MlExteriorDrawer>
      </div>
    </>
  );
};
